import { formatAttendance } from './summativeCompetency';

const asNumber = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const displayMark = value => {
  const parsed = asNumber(value);
  return parsed === null ? '-' : String(Math.round(parsed));
};

const displayAttendance = value => {
  const parsed = asNumber(value);
  return parsed === null ? '-' : `${formatAttendance(parsed)}%`;
};

const safeName = value => String(value || 'marksheet')
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase() || 'marksheet';

const escapeCsv = value => `"${String(value ?? '').replaceAll('"', '""')}"`;

const rowValues = row => {
  if (!row) return { formative: '-', summative: '-', total: '-', attendance: '-', remark: '-' };
  const formative = asNumber(row.formative_total_60);
  const summative = asNumber(row.summative_total_40 ?? row.converted_marks);
  const total = asNumber(row.overall_total_100 ?? (formative !== null && summative !== null ? formative + summative : null));
  return {
    formative: displayMark(formative),
    summative: displayMark(summative),
    total: displayMark(total),
    attendance: displayAttendance(row.attendance_percentage),
    remark: row.competency || '-',
  };
};

const documentMetadata = ({ institution, group }) => [
  ['Institution', institution?.name || 'Institution'],
  ['Motto', institution?.motto || ''],
  ['Faculty', group?.facultyName || ''],
  ['Department', group?.departmentName || ''],
  ['Programme', group?.programName || 'Programme'],
  ['Level', group?.programLevel || '-'],
  ['Intake', group?.intakeName || group?.intakeCode || '-'],
  ['Module(s)', group?.moduleLabel || '-'],
  ['Period', group?.period || '-'],
  ['Document', 'Historical academic progress consolidated marksheet'],
  ['Reference', group?.documentReference || institution?.official_reference_prefix || '-'],
  ['Generated', group?.generatedAt || new Date().toLocaleString()],
];

const loadImage = src => new Promise(resolve => {
  if (!src) { resolve(null); return; }
  try {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image.width && image.height ? image : null);
    image.onerror = () => resolve(null);
    image.src = src;
  } catch (_) { resolve(null); }
});

const drawFooter = (pdf, institution, pageWidth, pageHeight, margin, reference) => {
  const pageNumber = pdf.getCurrentPageInfo().pageNumber;
  pdf.setDrawColor(150, 160, 175);
  pdf.setLineWidth(0.2);
  pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(70, 80, 95);
  pdf.text(reference || institution?.official_reference_prefix || 'Official academic record', margin, pageHeight - 7);
  pdf.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
};

export const downloadHistoricalCohortMarksheetPdf = async ({ institution, group }) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = 297;
  const pageHeight = 210;
  const margin = 8;
  const tableRight = pageWidth - margin;
  const center = pageWidth / 2;
  const units = group?.units || [];
  const students = group?.students || [];
  const fixedWidths = [11, 38, 47];
  const fixedTotal = fixedWidths.reduce((sum, width) => sum + width, 0);
  const unitBlockWidth = units.length ? (tableRight - margin - fixedTotal) / units.length : 0;
  const unitSubWidths = unitBlockWidth ? [0.15, 0.15, 0.15, 0.15, 0.4].map(ratio => unitBlockWidth * ratio) : [];
  const fixedX = [margin, margin + fixedWidths[0], margin + fixedWidths[0] + fixedWidths[1]];
  const unitX = units.map((_, index) => margin + fixedTotal + (index * unitBlockWidth));
  const boundaryWidth = 0.65;
  const innerWidth = 0.18;
  const rowHeight = 7;
  const title = 'HISTORICAL ACADEMIC PROGRESS CONSOLIDATED MARKSHEET';

  const drawDocumentHeader = async startY => {
    let y = startY;
    const logo = await loadImage(institution?.logo_url);
    if (logo) {
      const height = 15;
      const width = (logo.width / logo.height) * height;
      pdf.addImage(logo, 'PNG', center - (width / 2), y, width, height);
      y += height + 3;
    }
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(13);
    pdf.text(String(institution?.name || 'INSTITUTION').toUpperCase(), center, y, { align: 'center' });
    y += 5;
    if (institution?.motto) {
      pdf.setFont('times', 'italic');
      pdf.setFontSize(8);
      pdf.text(String(institution.motto), center, y, { align: 'center', maxWidth: pageWidth - 30 });
      y += 4;
    }
    if (group?.facultyName) {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(8.5);
      pdf.text(String(group.facultyName).toUpperCase(), center, y, { align: 'center', maxWidth: pageWidth - 24 });
      y += 3.8;
    }
    if (group?.departmentName) {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(8);
      pdf.text(String(group.departmentName).toUpperCase(), center, y, { align: 'center', maxWidth: pageWidth - 24 });
      y += 3.8;
    }
    pdf.setDrawColor(30, 40, 55);
    pdf.setLineWidth(0.65);
    pdf.line(margin, y + 1, tableRight, y + 1);
    y += 6;
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10.5);
    pdf.text(title, center, y, { align: 'center' });
    y += 5;
    pdf.setFontSize(8);
    pdf.text(`${group?.programName || 'Programme'} - ${group?.programLevel || 'Level -'} - ${group?.intakeName || group?.intakeCode || 'Intake -'}`, center, y, { align: 'center', maxWidth: pageWidth - 25 });
    y += 4;
    pdf.setFont('times', 'normal');
    pdf.text(`${group?.moduleLabel || 'Module -'} - ${group?.period || 'Period -'} - ${students.length} student${students.length === 1 ? '' : 's'} - ${units.length} unit${units.length === 1 ? '' : 's'}`, center, y, { align: 'center', maxWidth: pageWidth - 25 });
    y += 4;
    pdf.setFontSize(6.8);
    pdf.text(`Document reference: ${group?.documentReference || institution?.official_reference_prefix || '-'} - Generated: ${group?.generatedAt || new Date().toLocaleString()}`, center, y, { align: 'center', maxWidth: pageWidth - 25 });
    return y + 6;
  };

  const drawTableHeader = startY => {
    const moduleGroups = [];
    units.forEach(unit => {
      const module = unit.module || 0;
      const last = moduleGroups[moduleGroups.length - 1];
      if (last && last.module === module) last.units.push(unit);
      else moduleGroups.push({ module, units: [unit] });
    });
    const heights = [7, 7, 7];
    const totalHeight = heights.reduce((sum, value) => sum + value, 0);
    const unitsStartX = margin + fixedTotal;
    pdf.setFillColor(232, 237, 244);
    pdf.rect(margin, startY, tableRight - margin, totalHeight, 'F');
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(boundaryWidth);
    pdf.rect(margin, startY, tableRight - margin, totalHeight);
    fixedWidths.forEach((width, index) => pdf.line(fixedX[index] + width, startY, fixedX[index] + width, startY + totalHeight));
    // The fixed student columns are row-spanned. Only the unit area gets the
    // two horizontal rules that separate module, unit-code, and mark headings.
    pdf.setDrawColor(175, 185, 198);
    pdf.setLineWidth(innerWidth);
    pdf.line(unitsStartX, startY + heights[0], tableRight, startY + heights[0]);
    pdf.line(unitsStartX, startY + heights[0] + heights[1], tableRight, startY + heights[0] + heights[1]);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(7.2);
    ['SN', 'Registration No.', 'Student Name'].forEach((label, index) => pdf.text(label, fixedX[index] + (fixedWidths[index] / 2), startY + 15, { align: 'center', maxWidth: fixedWidths[index] - 1 }));
    moduleGroups.forEach(moduleGroup => {
      const first = units.indexOf(moduleGroup.units[0]);
      const x = unitX[first];
      const width = moduleGroup.units.length * unitBlockWidth;
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(boundaryWidth);
      pdf.line(x, startY, x, startY + totalHeight);
      pdf.line(x + width, startY, x + width, startY + totalHeight);
      pdf.setFontSize(7.5);
      pdf.text(`MODULE ${moduleGroup.module || '-'}`, x + (width / 2), startY + 4.8, { align: 'center', maxWidth: width - 2 });
    });
    units.forEach((unit, index) => {
      const x = unitX[index];
      const width = unitBlockWidth;
      const marksWidth = unitSubWidths.slice(0, 4).reduce((sum, value) => sum + value, 0);
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(boundaryWidth);
      // Interior unit boundaries begin below the module heading so the module
      // cell remains one clean span over all of its units.
      pdf.line(x + width, startY + heights[0], x + width, startY + totalHeight);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(width < 28 ? 6 : 7.3);
      // Unit code occupies the complete unit row, including the remark area.
      pdf.text(unit.code || 'Unit', x + (width / 2), startY + heights[0] + 4.8, { align: 'center', maxWidth: width - 2 });
      pdf.setFontSize(width < 28 ? 5.2 : 6.5);
      pdf.text('REMARK', x + marksWidth + (unitSubWidths[4] / 2), startY + heights[0] + heights[1] + 4.8, { align: 'center', maxWidth: unitSubWidths[4] - 1 });
      pdf.setDrawColor(175, 185, 198);
      let subX = x;
      ['FA/60', 'SA/40', 'TT/100', 'AT%'].forEach((label, subIndex) => {
        if (subIndex > 0) pdf.line(subX, startY + heights[0] + heights[1], subX, startY + totalHeight);
        pdf.text(label, subX + (unitSubWidths[subIndex] / 2), startY + heights[0] + heights[1] + 4.8, { align: 'center', maxWidth: unitSubWidths[subIndex] - 1 });
        subX += unitSubWidths[subIndex];
      });
      pdf.setLineWidth(innerWidth);
      pdf.line(x + marksWidth, startY + heights[0] + heights[1], x + marksWidth, startY + totalHeight);
    });
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(boundaryWidth);
    pdf.line(margin, startY + totalHeight, tableRight, startY + totalHeight);
    return startY + totalHeight;
  };

  const drawStudentRow = (student, index, startY) => {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(7.2);
    const fixedValues = [String(index + 1), student.admissionNumber || '-', student.name || 'Unknown student'];
    fixedValues.forEach((value, columnIndex) => pdf.text(String(value), fixedX[columnIndex] + (fixedWidths[columnIndex] / 2), startY + 4.8, { align: 'center', maxWidth: fixedWidths[columnIndex] - 1 }));
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(boundaryWidth);
    pdf.line(margin, startY, margin, startY + rowHeight);
    fixedWidths.forEach((width, columnIndex) => {
      pdf.setLineWidth(boundaryWidth);
      pdf.line(fixedX[columnIndex] + width, startY, fixedX[columnIndex] + width, startY + rowHeight);
    });
    units.forEach((unit, unitIndex) => {
      const x = unitX[unitIndex];
      const values = rowValues(student.marks?.get(unit.key));
      const valuesArray = [values.formative, values.summative, values.total, values.attendance, values.remark];
      let cellX = x;
      pdf.setDrawColor(125, 135, 150);
      valuesArray.forEach((value, valueIndex) => {
        if (valueIndex > 0) {
          pdf.setLineWidth(innerWidth);
          pdf.line(cellX, startY, cellX, startY + rowHeight);
        }
        pdf.setFont('times', valueIndex === 4 && value !== '-' ? 'bold' : 'normal');
        pdf.setFontSize(valueIndex === 4 && unitBlockWidth < 28 ? 5.2 : 7.2);
        pdf.text(String(value), cellX + (unitSubWidths[valueIndex] / 2), startY + 4.8, { align: 'center', maxWidth: unitSubWidths[valueIndex] - 1 });
        cellX += unitSubWidths[valueIndex];
      });
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(boundaryWidth);
      pdf.line(x + unitBlockWidth, startY, x + unitBlockWidth, startY + rowHeight);
    });
    pdf.setLineWidth(boundaryWidth);
    pdf.line(tableRight, startY, tableRight, startY + rowHeight);
    pdf.setDrawColor(125, 135, 150);
    pdf.setLineWidth(innerWidth);
    pdf.line(margin, startY + rowHeight, tableRight, startY + rowHeight);
  };

  let y = await drawDocumentHeader(8);
  y = drawTableHeader(y);
  students.forEach((student, index) => {
    if (y + rowHeight > pageHeight - 18) {
      drawFooter(pdf, institution, pageWidth, pageHeight, margin, group?.documentReference);
      pdf.addPage('a4', 'landscape');
      y = drawTableHeader(12);
    }
    drawStudentRow(student, index, y);
    y += rowHeight;
  });
  if (!students.length) {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(8);
    pdf.text('No students match the selected programme, level and intake.', center, y + 5, { align: 'center' });
    y += rowHeight;
  }
  y += 5;
  pdf.setFont('times', 'bold');
  pdf.setFontSize(7);
  pdf.text('KEY:', margin, y);
  pdf.setFont('times', 'normal');
  pdf.text('A. Mastery - Attained mastery - Proficient - Competent - NYC - Not Yet Competent - NYC-F - Formative - NYC-S - Summative - NYC-AT - Attendance', margin + 10, y, { maxWidth: tableRight - margin - 10 });
  drawFooter(pdf, institution, pageWidth, pageHeight, margin, group?.documentReference);
  const filename = `historical-consolidated-marksheet-${safeName(group?.programName)}-${safeName(group?.intakeCode || group?.intakeName || 'intake')}.pdf`;
  pdf.save(filename);
};

export const downloadHistoricalCohortMarksheetCsv = ({ institution, group }) => {
  const units = group?.units || [];
  const students = group?.students || [];
  const lines = documentMetadata({ institution, group }).map(([label, value]) => [label, value]);
  lines.push([]);
  const headers = ['SN', 'Registration No.', 'Student Name'];
  units.forEach(unit => {
    headers.push(`${unit.code} FA/60`, `${unit.code} SA/40`, `${unit.code} TT/100`, `${unit.code} AT%`, `${unit.code} REMARK`);
  });
  lines.push(headers);
  students.forEach((student, index) => {
    const line = [index + 1, student.admissionNumber || '', student.name || 'Unknown student'];
    units.forEach(unit => {
      const values = rowValues(student.marks?.get(unit.key));
      line.push(values.formative, values.summative, values.total, values.attendance, values.remark);
    });
    lines.push(line);
  });
  const csv = `\uFEFF${lines.map(line => line.map(escapeCsv).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `historical-consolidated-marksheet-${safeName(group?.programName)}-${safeName(group?.intakeCode || group?.intakeName || 'intake')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
